
import { Home, House, Plus, Users, User, Clock, Bell, BellRing } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useDashboardData } from "@/hooks/useDashboardData";
import { useAuth } from "@/contexts/AuthContext";
import { useMobileDetect } from "@/hooks/useMobileDetect";
import { useTranslation } from "react-i18next";
import { useFriends } from "@/hooks/useFriends";

const BottomNavigation = () => {
  const { user } = useAuth();
  const { t } = useTranslation('common');
  const location = useLocation();
  const currentPath = location.pathname;
  const { data: dashboardData } = useDashboardData();
  const { isMobileOrTablet } = useMobileDetect();
  const { pendingRequests, loading } = useFriends();
  
  // Don't render navigation if user is not authenticated
  if (!user) {
    return null;
  }
  
  // Don't render BottomNavigation for timeline routes (TimelineNavigation is handled in Timeline page)
  const isTimelineMode = currentPath.startsWith('/timeline');
  if (isTimelineMode) {
    return null;
  }
  
  const isActive = (path: string) => {
    return currentPath === path;
  };

  return (
    <div className="h-full w-full flex items-center justify-center px-4 pb-[env(safe-area-inset-bottom)]">
      <div className="flex justify-around items-center w-full max-w-md">
        <Link 
          to="/dashboard" 
          className={cn(
            "flex flex-col items-center p-3 rounded-lg transition-colors",
            isActive('/dashboard') ? "text-uplivion-blue" : "text-gray-500 hover:text-gray-700"
          )}
        >
          {isActive('/dashboard') ? <House className="h-6 w-6" /> : <Home className="h-6 w-6" />}
          {!isMobileOrTablet() && <span className="text-xs mt-1">{t('navigation.dashboard')}</span>}
        </Link>
        
        <Link 
          to="/friends" 
          className={cn(
            "flex flex-col items-center p-3 rounded-lg transition-colors",
            isActive('/friends') ? "text-uplivion-blue" : "text-gray-500 hover:text-gray-700"
          )}
        >
          <div className="relative">
            <Users className="h-6 w-6" />
            {!loading && pendingRequests.length > 0 && (
              <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-medium">
                {pendingRequests.length > 9 ? '9+' : pendingRequests.length}
              </div>
            )}
          </div>
          {!isMobileOrTablet() && <span className="text-xs mt-1">{t('navigation.friends')}</span>}
        </Link>
        
        <Link 
          to="/timeline" 
          className={cn(
            "flex flex-col items-center p-3 rounded-lg transition-colors",
            isActive('/timeline') ? "text-uplivion-blue" : "text-gray-500 hover:text-gray-700"
          )}
        >
          <Clock className="h-6 w-6" />
          {!isMobileOrTablet() && <span className="text-xs mt-1">{t('navigation.timeline')}</span>}
        </Link>
        
        <Link 
          to="/notifications" 
          className={cn(
            "flex flex-col items-center p-3 rounded-lg transition-colors relative",
            isActive('/notifications') ? "text-uplivion-blue" : "text-gray-500 hover:text-gray-700"
          )}
        >
          <div className="relative">
            <BellRing className="h-6 w-6" /> 
          </div>
          {!isMobileOrTablet() && <span className="text-xs mt-1">{t('navigation.notifications')}</span>}
        </Link>
        
        <Link 
          to="/profile" 
          className={cn(
            "flex flex-col items-center p-3 rounded-lg transition-colors",
            isActive('/profile') ? "text-uplivion-blue" : "text-gray-500 hover:text-gray-700"
          )}
        >
          <User className="h-6 w-6" />
          {!isMobileOrTablet() && <span className="text-xs mt-1">{t('navigation.profile')}</span>}
        </Link>
      </div>
    </div>
  );
};

export default BottomNavigation;
